// src/components/files/FilePreview.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  Box,
  IconButton,
  Typography,
  Button,
  CircularProgress,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import MovieIcon from '@mui/icons-material/Movie';
import { useTranslation } from 'react-i18next';
import { useFileManager } from '../../contexts/FileManagerContext';
import { FileDocument } from '../../types/files';
import { formatFileSize } from '../../services/fileService';

interface FilePreviewProps {
  open: boolean;
  onClose: () => void;
  file: FileDocument | null;
  files: FileDocument[];
  currentIndex: number;
  onNavigate: (index: number) => void;
}

const FilePreview: React.FC<FilePreviewProps> = ({
  open,
  onClose,
  file,
  files,
  currentIndex,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const { downloadDocument } = useFileManager();
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Reset zoom when file changes
  useEffect(() => {
    setZoom(1);
    setImageError(false);
  }, [file]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
        onNavigate(currentIndex - 1);
      } else if (e.key === 'ArrowRight' && currentIndex < files.length - 1) {
        onNavigate(currentIndex + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, currentIndex, files.length, onNavigate, onClose]);

  const handleDownload = useCallback(async () => {
    if (!file) return;
    setLoading(true);
    try {
      await downloadDocument(file.id, file.filename);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setLoading(false);
    }
  }, [file, downloadDocument]);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  const getFileType = (mimeType: string | undefined): 'image' | 'pdf' | 'audio' | 'video' | 'other' => {
    if (!mimeType) return 'other';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType === 'application/pdf') return 'pdf';
    if (mimeType.startsWith('audio/')) return 'audio';
    if (mimeType.startsWith('video/')) return 'video';
    return 'other';
  };

  const getFileIcon = (mimeType: string | undefined) => {
    const type = getFileType(mimeType);
    switch (type) {
      case 'image':
        return <ImageIcon sx={{ fontSize: 120, color: '#10b981' }} />;
      case 'pdf':
        return <PictureAsPdfIcon sx={{ fontSize: 120, color: '#ef4444' }} />;
      case 'audio':
        return <AudiotrackIcon sx={{ fontSize: 120, color: '#ec4899' }} />;
      case 'video':
        return <MovieIcon sx={{ fontSize: 120, color: '#8b5cf6' }} />;
      default:
        return <InsertDriveFileIcon sx={{ fontSize: 120, color: '#64748b' }} />;
    }
  };

  const renderContent = () => {
    if (!file) return null;

    const fileType = getFileType(file.file_type);
    const fileUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/${file.file_url}`;

    switch (fileType) {
      case 'image':
        return (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
              p: 2,
            }}
          >
            {imageError ? (
              <Box sx={{ textAlign: 'center' }}>
                {getFileIcon(file.file_type)}
                <Typography color="text.secondary" sx={{ mt: 2 }}>
                  {t('fileManager.preview.imageLoadError', "Impossible de charger l'image")}
                </Typography>
              </Box>
            ) : (
              <img
                src={fileUrl}
                alt={file.filename}
                onError={() => setImageError(true)}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  transform: `scale(${zoom})`,
                  transition: 'transform 0.2s ease',
                  cursor: zoom < 3 ? 'zoom-in' : 'zoom-out',
                }}
                onClick={() => (zoom < 3 ? handleZoomIn() : setZoom(1))}
              />
            )}
          </Box>
        );

      case 'pdf':
        return (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <iframe
              src={fileUrl}
              title={file.filename}
              style={{
                flex: 1,
                border: 'none',
                width: '100%',
                height: '100%',
              }}
            />
          </Box>
        );

      case 'audio':
        return (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
            }}
          >
            {getFileIcon(file.file_type)}
            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              {file.filename}
            </Typography>
            <audio
              controls
              src={fileUrl}
              style={{ width: '100%', maxWidth: 500 }}
            >
              {t('fileManager.preview.audioNotSupported', 'Votre navigateur ne supporte pas la lecture audio')}
            </audio>
          </Box>
        );

      case 'video':
        return (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <video
              controls
              src={fileUrl}
              style={{ maxWidth: '100%', maxHeight: '100%' }}
            >
              {t('fileManager.preview.videoNotSupported', 'Votre navigateur ne supporte pas la lecture video')}
            </video>
          </Box>
        );

      default:
        // Other file types - show metadata and download button
        return (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
            }}
          >
            {getFileIcon(file.file_type)}

            <Typography variant="h5" fontWeight={600} sx={{ mt: 3, textAlign: 'center' }}>
              {file.filename}
            </Typography>

            <Box
              sx={{
                mt: 3,
                p: 3,
                bgcolor: alpha('#fff', 0.1),
                borderRadius: 2,
                minWidth: 300,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography color="text.secondary">
                  {t('fileManager.preview.type', 'Type')}
                </Typography>
                <Typography fontWeight={500}>
                  {file.file_type || t('fileManager.preview.unknown', 'Inconnu')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography color="text.secondary">
                  {t('fileManager.preview.size', 'Taille')}
                </Typography>
                <Typography fontWeight={500}>
                  {file.file_size ? formatFileSize(file.file_size) : '—'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">
                  {t('fileManager.preview.created', 'Cree le')}
                </Typography>
                <Typography fontWeight={500}>
                  {new Date(file.created_at).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              size="large"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={loading}
              sx={{ mt: 4 }}
            >
              {loading ? (
                <CircularProgress size={20} />
              ) : (
                t('fileManager.preview.download', 'Telecharger')
              )}
            </Button>
          </Box>
        );
    }
  };

  if (!file) return null;

  const fileType = getFileType(file.file_type);
  const showZoomControls = fileType === 'image' && !imageError;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          color: 'white',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {file.filename}
          </Typography>
          {file.file_size && (
            <Typography variant="body2" color="text.secondary">
              {formatFileSize(file.file_size)}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Zoom controls for images */}
          {showZoomControls && (
            <>
              <IconButton onClick={handleZoomOut} disabled={zoom <= 0.5} sx={{ color: 'white' }}>
                <ZoomOutIcon />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
                {Math.round(zoom * 100)}%
              </Typography>
              <IconButton onClick={handleZoomIn} disabled={zoom >= 3} sx={{ color: 'white' }}>
                <ZoomInIcon />
              </IconButton>
            </>
          )}

          {/* Download button */}
          <IconButton onClick={handleDownload} disabled={loading} sx={{ color: 'white' }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          </IconButton>

          {/* Close button */}
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'stretch',
          overflow: 'hidden',
        }}
      >
        {/* Previous button */}
        {currentIndex > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 1,
            }}
          >
            <IconButton
              onClick={() => onNavigate(currentIndex - 1)}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
              }}
            >
              <ChevronLeftIcon fontSize="large" />
            </IconButton>
          </Box>
        )}

        {/* Main content */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {renderContent()}
        </Box>

        {/* Next button */}
        {currentIndex < files.length - 1 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 1,
            }}
          >
            <IconButton
              onClick={() => onNavigate(currentIndex + 1)}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
              }}
            >
              <ChevronRightIcon fontSize="large" />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Footer - file counter */}
      {files.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'rgba(255, 255, 255, 0.1)',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {currentIndex + 1} / {files.length}
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default FilePreview;
